import { memo, useCallback } from 'react'
import { ChevronRight } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { TodoMark } from '@highlight/ui'
import { useForYouLingerControls, useForYouLingerEntries } from '@renderer/overlay/hooks/for-you-linger'
import { SidepanelCellRow } from '@renderer/overlay/Sidepanel/components/cells/row'
import {
  SIDEPANEL_FILLED_HOVER_SURFACE,
  TODO_MARK,
  TODO_TITLE,
} from '@renderer/overlay/Sidepanel/components/cells/surface'
import { TodoTag, TodoTags } from '@renderer/overlay/Sidepanel/components/cells/todo-tags'
import type { TodoEntry } from '../sections/todos-order'

/**
 * Every notification as one to-do atop To-dos. A click opens it to show them
 * under it. Ticking its mark archives them all, and unticking it during the
 * linger restores them.
 */
export const NotificationsTodo = memo(function NotificationsTodo({
  todos,
  open,
  feedKey,
  onToggle,
}: {
  todos: Array<TodoEntry>
  open: boolean
  feedKey: string
  onToggle: () => void
}) {
  const { start: startLinger, cancel: cancelLinger } = useForYouLingerControls()
  const lingers = useForYouLingerEntries()

  const chats = todos.flatMap((todo) => (todo.kind === 'chat' ? [todo] : []))
  const allLingering = chats.every((todo) => lingers.has(todo.id))
  const title = chats.length === 1 ? 'Read 1 notification' : `Read ${chats.length} notifications`

  const archiveAll = useCallback(() => {
    for (const todo of chats) {
      if (lingers.has(todo.id)) continue
      startLinger({
        chat: todo.chat,
        kind: 'archived',
        feedKey,
        position: todo.feedPosition,
        startedAt: Date.now(),
        sharesTodosCountdown: true,
      })
    }
  }, [chats, feedKey, lingers, startLinger])
  const restoreAll = useCallback(() => {
    for (const todo of chats) {
      if (lingers.get(todo.id)?.sharesTodosCountdown) cancelLinger(todo.id)
    }
  }, [cancelLinger, chats, lingers])

  return (
    <SidepanelCellRow
      className={`py-1.5 ${SIDEPANEL_FILLED_HOVER_SURFACE}`}
      icon={
        <TodoMark
          progress="ready"
          checked={allLingering}
          onChange={(event) => (event.currentTarget.checked ? archiveAll() : restoreAll())}
          // The row opens on click and Enter/Space, so the mark keeps its own.
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          aria-label={allLingering ? `Undo: ${title}` : `Done: ${title}`}
          className={`mt-[3px] ${TODO_MARK}`}
        />
      }
      title={title}
      titleClassName={twMerge(
        `line-clamp-none ${TODO_TITLE}`,
        allLingering && 'decoration-foreground-placeholder line-through',
      )}
      showChevron={false}
      trailing={
        <TodoTags>
          <TodoTag
            icon={<ChevronRight className={twMerge('transition-transform', open && 'rotate-90')} />}
            label={open ? 'Hide notifications' : 'Show notifications'}
          />
        </TodoTags>
      }
      onClick={onToggle}
    />
  )
})
